import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { PlayIconButton } from "./play-icon-button";
import { SampleSeasonAsset } from "./sample-season-asset";
import * as Styled from "./style";
import { useHandlePlayBtnPressed } from "~/components/blocks/product/detail/header/video-season-header/hooks/use-handle-play-btn-pressed";
import type { VideoSeasonType } from "~/components/blocks/product/type-guard";
import { isTouchDisplay } from "~/const/device";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useOutsidePointerdown } from "~/hooks/use-outside-pointerdown";
import { useQueryString } from "~/hooks/use-query-string";
import { G_VideoSeasonType } from "~/lib/graphql";

let timerId: number;

type Episode = NonNullable<VideoSeasonType["episode"]>;
type FocusedEpisode = {
  id: Episode["id"];
  episodeTitle: Episode["episodeTitle"];
  episodeNumber: Episode["episodeNumber"];
  episodeNumberName?: Episode["episodeNumberName"];
  playInfo?: Episode["playInfo"];
};

type VideoSeasonHeaderProps = {
  videoSeasonData: VideoSeasonType;
};

/**
 * 作品末端のプレビュー動画を管理するコンポーネント
 *
 * @機能要件
 * - 再生ボタン : 視聴権の有無で振る舞いが異なるので、視聴権の有無に応じて場合分けを行うこと
 * - 配信前（isBeingDelivered）の場合 : 再生ボタンを「非表示」にすること
 * - 視聴権ありの場合 : 再生ボタンを「表示」すること（7月12日以降、提供予定）
 * - 視聴権あり and レジュームありの場合 : 〜 を続きから再生 と再生アイコンの下に表示すること（7月12日以降、提供予定）
 * - 視聴権なし and PPVのみの場合 : 再生ボタン + 購入して再生 という文言を表示すること（7月12日以降、提供予定）
 */
export const VideoSeasonHeader = memo((props: VideoSeasonHeaderProps) => {
  const { videoSeasonData } = props;

  const { season, content } = useQueryString();
  const { isSpSize } = useMatchMedia();

  const [isPlayBtnDisplay, setIsPlayBtnDisplay] = useState(true);
  const showPlayBtn = useCallback(() => !isSpSize && setIsPlayBtnDisplay(true), []);
  const hidePlayBtn = useCallback(() => !isSpSize && setIsPlayBtnDisplay(false), []);

  const { ref: articleRef } = useOutsidePointerdown<HTMLElement>({
    onOutsidePointerDown: () => hidePlayBtn(),
  });

  const {
    seasonType,
    packageImage,
    keyVisualImage,
    keyVisualWithoutLogoImage,
    episode,
    episodes: { edges: mainEpisodeEdges },
    pvEpisode: { edges: pvEpisodeEdges },
    continueWatching,
    rating: { category },
  } = videoSeasonData;

  const sampleMovieUrl = useMemo(() => {
    if (pvEpisodeEdges.length === 0) return "";
    return pvEpisodeEdges[0].node.sampleMovie;
  }, [videoSeasonData.id]);

  const shouldUseContinueWatching = () => {
    const isMultiEpisodes = seasonType === G_VideoSeasonType.MultiEpisodes;
    return !!continueWatching && isMultiEpisodes && mainEpisodeEdges[0].node.ppvProducts.length > 0;
  };

  const {
    episodeNumberName,
    id: episodeContentId,
    episodeTitle,
    episodeNumber,
    playInfo,
  } = useMemo((): FocusedEpisode => {
    const initialContent = { id: "", episodeNumberName: "", episodeTitle: "", episodeNumber: 0 };
    // エピソード末端の複数話の場合
    if (content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      return episode ? episode : initialContent;
    }
    // エピソード末端でない、かつ話がない場合はカラを返す
    if (mainEpisodeEdges.length === 0) return initialContent;
    const { node } = mainEpisodeEdges[0];

    // エピソード末端でない複数話 もしくは 単数話の場合（PPV商品の場合はcontinueWatchingから取得）
    if (!continueWatching || !shouldUseContinueWatching()) {
      return {
        id: node.id,
        episodeTitle: node.episodeTitle,
        episodeNumber: node.episodeNumber,
        episodeNumberName: node.episodeNumberName,
      };
    }
    return { ...continueWatching.content, id: continueWatching.contentId };
  }, [season, content, videoSeasonData.id, episode?.id, continueWatching?.id]);

  /** エピソード末端（?season=AAA&content=BBB）の場合のみ使用する */
  const episodeImage = useMemo(() => {
    // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
    if (!episode) {
      // エピソード末端でない複数話かつPPV商品の場合はcontinueWatchingから取得
      return !!continueWatching && shouldUseContinueWatching() ? continueWatching.content.episodeImage || "" : "";
    }

    // レジューム情報が存在する場合
    if (continueWatching !== undefined && continueWatching !== null) {
      const {
        contentId,
        content: { episodeImage: continueEpisodeImage },
      } = continueWatching;

      // MEMO : continueWatchingとクエリパラメータのcontentIdが同じ場合continueWatching、異なる場合はクエリパラメータのcontentIdの情報を使用
      return contentId === content ? continueEpisodeImage : episode.episodeImage;
    }
    // レジューム情報が存在しない場合の処理
    return content !== undefined ? episode.episodeImage : "";
  }, [episodeContentId]);

  /** continueWatching以外のパターンの視聴権利があるかどうかを判定する */
  const isStreamable = useMemo(() => {
    // エピソード末端の複数話の場合
    if (content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐためにオプショナルチェイニングを使用
      return episode?.viewingRights?.isStreamable ?? false;
    }

    // エピソード末端でない複数話 もしくは 単数話の場合
    if (mainEpisodeEdges.length > 0) {
      return mainEpisodeEdges[0].node.viewingRights.isStreamable;
    }

    return false;
  }, [episodeContentId]);

  /** PPV商品があるかどうかを判定する */
  const ppvProducts = useMemo(() => {
    if (content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      return episode?.ppvProducts ?? [];
    }

    if (mainEpisodeEdges.length > 0) {
      return mainEpisodeEdges[0].node.ppvProducts;
    }

    return [];
  }, [episodeContentId]);

  /** SVOD商品があるかどうかを判定する */
  const svodProduct = useMemo(() => {
    if (content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      return episode?.svodProduct;
    }

    if (mainEpisodeEdges.length > 0) {
      return mainEpisodeEdges[0].node.svodProduct;
    }

    return;
  }, [episodeContentId]);

  const { onPlayBtnPressed } = useHandlePlayBtnPressed({
    episodeContentId,
    continueWatching,
    ppvProducts,
    isStreamable,
    season,
    svodProduct,
    content,
  });

  useEffect(() => {
    // MEMO（kimura）: 予告動画を再生しない端末の場合、3秒後に再生/購入ボタンを自動で消すことでユーザーの混乱を招いたので以下の処理を記載する
    if (isSpSize) return;

    timerId = window.setTimeout(() => hidePlayBtn(), 3000);
    return () => clearTimeout(timerId);
  }, [isSpSize]);

  return (
    <Styled.ArtProductHeaderWrap
      ref={articleRef}
      onMouseOut={() => hidePlayBtn()}
      onMouseMove={() => !isPlayBtnDisplay && showPlayBtn()}
      onClick={() => isTouchDisplay && showPlayBtn()}
    >
      <Styled.DivOverlay />
      <SampleSeasonAsset
        sampleMovieUrl={sampleMovieUrl}
        episodeImage={episodeImage}
        packageImage={packageImage}
        keyVisualImage={keyVisualImage}
        keyVisualWithoutLogoImage={keyVisualWithoutLogoImage}
        videoRatingCategory={category}
      />
      <PlayIconButton
        seasonType={seasonType}
        continueWatching={continueWatching}
        isStreamable={isStreamable}
        ppvProducts={ppvProducts}
        svodProduct={svodProduct}
        episodeContentId={episodeContentId}
        episodeTitle={episodeTitle}
        episodeNumber={`${episodeNumber}`}
        episodeNumberName={episodeNumberName}
        playInfo={playInfo}
        isPlayBtnDisplay={isPlayBtnDisplay}
        onPlayBtnPressed={onPlayBtnPressed}
      />
    </Styled.ArtProductHeaderWrap>
  );
});

VideoSeasonHeader.displayName = "VideoSeasonHeader";
